// Copyright (c) Ping. All rights reserved.
//
// Assembles the per-pane guide: a walkthrough of the sections inside one
// settings pane, launched from that pane's floating guide button. Each pane's
// section list lives in ./paneSections/<pane>.ts; add a section there (and a
// matching `testID` on the row) to extend a pane's guide.

import type { SettingsTabId } from '@ping/ui/src/settings-modal/types';

import { accountSections } from './paneSections/account';
import { appearanceSections } from './paneSections/appearance';
import { encryptionSections } from './paneSections/encryption';
import { helpAboutSections } from './paneSections/helpAbout';
import { keyboardSections } from './paneSections/keyboard';
import { linkingSections } from './paneSections/linking';
import { notificationsSections } from './paneSections/notifications';
import { preferencesSections } from './paneSections/preferences';
import { recoverySections } from './paneSections/recovery';
import { securityPrivacySections } from './paneSections/securityPrivacy';
import { sessionSections } from './paneSections/session';
import { sidebarSections } from './paneSections/sidebar';
import { storageSections } from './paneSections/storage';
import { voiceVideoSections } from './paneSections/voiceVideo';
import type { GuidedStep } from './GuidedTourTypes';
import type { PaneSection } from './paneGuideTypes';

const SECTIONS: Record<SettingsTabId, readonly PaneSection[]> = {
  account: accountSections,
  session: sessionSections,
  appearance: appearanceSections,
  notifications: notificationsSections,
  preferences: preferencesSections,
  keyboard: keyboardSections,
  sidebar: sidebarSections,
  voiceVideo: voiceVideoSections,
  storage: storageSections,
  securityPrivacy: securityPrivacySections,
  encryption: encryptionSections,
  linking: linkingSections,
  recovery: recoverySections,
  helpAbout: helpAboutSections,
};

// `section` was authored either as a bare suffix ("profile") or as the full
// testID ("settings-account-profile"); normalise both to the DOM anchor.
function anchorFor(pane: SettingsTabId, section: string): string {
  const testId = section.startsWith('settings-') ? section : `settings-${pane}-${section}`;
  return `[data-testid="${testId}"]`;
}

// Re-select the pane so its content is on screen. Runs as the first step is
// entered, AFTER the DOM has committed. The user launched the guide from this
// pane's button, so it is already active; this is just a safety net.
function selectPane(pane: SettingsTabId): void {
  if (typeof document === 'undefined') {
    return;
  }
  document.querySelector<HTMLElement>(`[data-testid="settings-tab-${pane}"]`)?.click();
}

/**
 * Pure builder: one step per section of the pane, in order. The anchor is
 * resolved by Reactour when the step becomes active (post-commit), so this must
 * NOT read the DOM itself (probing the DOM during render is unreliable in
 * React 18 and would silently drop steps).
 */
export function buildPaneGuideSteps(pane: SettingsTabId): GuidedStep[] {
  const sections = SECTIONS[pane] ?? [];
  return sections.map((s, index) => {
    const step: GuidedStep = {
      id: `pane-${pane}-${s.section}`,
      selector: anchorFor(pane, s.section),
      title: s.title,
      body: s.body,
      position: 'left',
    };
    // First step re-selects the pane as a safety net.
    return index === 0 ? { ...step, onEnter: () => selectPane(pane) } : step;
  });
}
